/* eslint-disable max-len */

/**
 * Center-aligned content with animating logos.
 * Show when at least 25% of logo wrapper is visible.
 * Hide when 80% of logo wrapper is out of viewport (20% or less visible).
 * On exit, all logos pour to the same point at bottom along a curved path (water-pour effect).
 */

const SECTION_SELECTOR = '.e-stn--center-aligned-content-with-animating-logos';
const LOGO_WRAPPER_SELECTOR = '.e-div--logo-wrapper';
const CONTENT_WRAPPER_SELECTOR = '.e-div--content-wrapper';
const LOGO_ITEM_SELECTOR = '.e-img';
const RANDOM_DELAY_MAX_MS = 500;
const POUR_DURATION_MS = 1100;
const POUR_ARC_HEIGHT = 90; // upward arc (px) before falling into bucket
const LOGO_PSEUDO_HEIGHT_OFFSET_PX = 71;
const SHOW_VISIBILITY_RATIO = 0.2;
const HIDE_VISIBILITY_RATIO = 0.3;

// Deterministic stagger (index-based) to avoid Math.random() for static analysis / security rules
function setLogoEnterDelays( logos ) {
  logos.forEach( ( el, index ) => {
    const delay = ( index * 97 ) % RANDOM_DELAY_MAX_MS;
    el.style.transitionDelay = `${delay}ms`;
  } );
}

// Parabolic path: x(t)=dx*t, y(t)=dy*t + 4*k*t*(1-t); k negative = arc upward then fall
function pourKeyframes( dx, dy ) {
  const k = -POUR_ARC_HEIGHT;
  const steps = [ 0, 0.2, 0.4, 0.6, 0.8, 1 ];
  return steps.map( ( t ) => {
    const x = dx * t;
    const y = dy * t + 4 * k * t * ( 1 - t );
    const scale = 1 - 0.8 * t;
    return {
      transform: `translate(${x}px, ${y}px) scale(${scale})`,
      offset: t,
    };
  } );
}

function setLogosPourToBottom( section, logos, onAllFinished ) {
  const sectionRect = section.getBoundingClientRect();
  const targetX = sectionRect.left + sectionRect.width / 2;
  const targetY = sectionRect.bottom;
  let finished = 0;

  logos.forEach( ( el ) => {
    if ( el._pourAnimation ) {
      el._pourAnimation.cancel();
    }
    el.style.transitionDelay = '0ms';
    el.style.transition = 'none';
    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const dx = targetX - centerX;
    const dy = targetY - centerY;
    const keyframes = pourKeyframes( dx, dy );
    el._pourAnimation = el.animate( keyframes, {
      duration: POUR_DURATION_MS,
      easing: 'ease-in',
      fill: 'forwards',
    } );
    el._pourAnimation.onfinish = () => {
      el.style.transform = keyframes[ keyframes.length - 1 ].transform;
      finished += 1;
      if ( finished === logos.length && onAllFinished ) {
        onAllFinished();
      }
    };
  } );
}

function applyLogoWrapperPseudoHeight( section ) {
  const contentWrapper = section.querySelector( CONTENT_WRAPPER_SELECTOR );
  const logoWrapper = section.querySelector( LOGO_WRAPPER_SELECTOR );
  if ( !contentWrapper || !logoWrapper ) return;
  const contentHeight = contentWrapper.offsetHeight;
  const pseudoHeight = contentHeight + LOGO_PSEUDO_HEIGHT_OFFSET_PX;
  logoWrapper.style.setProperty( '--logo-pseudo-height', `${pseudoHeight}px` );
}

function clearLogoTransforms( logos ) {
  logos.forEach( ( el ) => {
    if ( el._pourAnimation ) {
      el._pourAnimation.cancel();
      el._pourAnimation = null;
    }
    el.style.removeProperty( 'transition' );
    el.style.removeProperty( 'transition-delay' );
    el.style.removeProperty( 'transform' );
  } );
}

function showLogosInView( section, logos, state ) {
  state.animationCycle += 1;
  state.visibilityState = 'visible';
  section.classList.remove( 'is-exiting' );
  clearLogoTransforms( logos );
  section.classList.add( 'is-in-view' );
  setLogoEnterDelays( logos );
}

function hideLogosOutOfView( section, logos, state ) {
  state.animationCycle += 1;
  const currentCycle = state.animationCycle;
  state.visibilityState = 'exiting';
  section.classList.add( 'is-exiting' );
  setLogosPourToBottom( section, logos, () => {
    if ( currentCycle !== state.animationCycle || state.visibilityState !== 'exiting' ) {
      return;
    }
    section.classList.remove( 'is-in-view' );
    state.visibilityState = 'hidden';
  } );
}

function handleLogoVisibilityEntry( entry, section, logos, state ) {
  const { intersectionRatio } = entry;

  if ( intersectionRatio >= SHOW_VISIBILITY_RATIO && state.visibilityState !== 'visible' ) {
    showLogosInView( section, logos, state );
    return;
  }

  if ( intersectionRatio <= HIDE_VISIBILITY_RATIO && state.visibilityState === 'visible' ) {
    hideLogosOutOfView( section, logos, state );
  }
}

function observeLogoWrapperVisibility( section, logoWrapper, logos, state ) {
  const observer = new IntersectionObserver(
    ( entries ) => {
      entries.forEach( ( entry ) => {
        handleLogoVisibilityEntry( entry, section, logos, state );
      } );
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: [ 0, HIDE_VISIBILITY_RATIO, SHOW_VISIBILITY_RATIO, 1 ],
    }
  );

  // Observe the section instead of logoWrapper — the logo wrapper is 992px wide
  // which skews the intersection ratio on narrow viewports, preventing the
  // show threshold from being reached.
  observer.observe( section );
}

function bindHorizontalResizeHandler( section, logos ) {
  // On horizontal resize only: cancel pour and clear transforms; update logo pseudo height (iOS fires resize on scroll when address bar shows/hides)
  let resizeTicking = false;
  let lastInnerWidth = window.innerWidth;
  window.addEventListener( 'resize', () => {
    if ( resizeTicking ) return;
    const width = window.innerWidth;
    if ( width === lastInnerWidth ) return;
    lastInnerWidth = width;
    resizeTicking = true;
    requestAnimationFrame( () => {
      clearLogoTransforms( logos );
      applyLogoWrapperPseudoHeight( section );
      resizeTicking = false;
    } );
  } );
}

function initCenterAlignedLogoScale() {
  const section = document.querySelector( SECTION_SELECTOR );
  if ( !section ) return;

  const logoWrapper = section.querySelector( LOGO_WRAPPER_SELECTOR );
  if ( !logoWrapper ) return;

  const logos = logoWrapper.querySelectorAll( LOGO_ITEM_SELECTOR );
  const state = {
    visibilityState: 'hidden', // hidden | visible | exiting
    animationCycle: 0,
  };

  observeLogoWrapperVisibility( section, logoWrapper, logos, state );

  applyLogoWrapperPseudoHeight( section );
  bindHorizontalResizeHandler( section, logos );
}

function runCenterAlignedLogo() {
  if ( document.querySelector( '.editor-styles-wrapper' ) ) return;
  initCenterAlignedLogoScale();
}

if ( document.readyState === 'loading' ) {
  document.addEventListener( 'DOMContentLoaded', runCenterAlignedLogo );
} else {
  runCenterAlignedLogo();
}
